import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Service,
  ServiceDetailsResponse,
  SummaryResponse,
  Announcement,
} from '@incidents/contracts';
import { useApi, apiCalls } from '../api.ts';
import { useI18n } from '../i18n.tsx';
import { useDataChanged } from '../socket.ts';
import { clientTZ } from '../dates.ts';
import { Legend } from './Legend.tsx';
import { Timeline, type SlotInfo } from './Timeline.tsx';
import { ServiceDetailsInline } from './ServiceDetails.tsx';
import { AnnouncementBanner } from './AnnouncementBanner.tsx';

function HeaderCard({ summary, lang }: { summary: SummaryResponse | null; lang: string }) {
  const { t } = useI18n();
  return (
    <div className="bg-white rounded-xl shadow p-4 mb-4 flex items-center justify-between">
      <div className="font-semibold text-slate-700">
        {t('header.statusByService', 'Status by service :')}{' '}
        {new Date().toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { timeZone: clientTZ })}
      </div>
      {summary?.allOk ? (
        <div className="flex items-center text-emerald-600 gap-2">
          <i className="fas fa-check-circle" /> OK
        </div>
      ) : (
        <div className="flex items-center text-rose-600 gap-2">
          <i className="fas fa-triangle-exclamation" /> Issues
        </div>
      )}
    </div>
  );
}

interface RowState {
  service: Service;
  details: ServiceDetailsResponse | null;
}

export function Services() {
  const api = useApi();
  const { lang } = useI18n();
  const [rows, setRows] = useState<RowState[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [openServiceId, setOpenServiceId] = useState<number | null>(null);
  const [selectedSlotByService, setSelectedSlotByService] = useState<Record<number, SlotInfo>>({});
  const refreshRef = useRef<() => void>(() => undefined);

  const refresh = useCallback(async () => {
    try {
      const [services, anns, sum] = await Promise.all([
        apiCalls.listServices(api),
        apiCalls.listAnnouncements(api),
        apiCalls.summary(api),
      ]);
      setAnnouncements(anns);
      setSummary(sum);
      const details = await Promise.all(
        services.map(async (s) => {
          try {
            return await apiCalls.getServiceDetails(api, s.name);
          } catch {
            return null;
          }
        }),
      );
      setRows(services.map((service, i) => ({ service, details: details[i] ?? null })));
    } catch (err) {
      console.error('refresh failed', err);
    }
  }, [api]);

  // Sync the local timezone to the tenant's default_timezone (best-effort).
  useEffect(() => {
    apiCalls.postTz(api, clientTZ).catch(() => undefined);
    document.cookie = `tz=${encodeURIComponent(clientTZ)}; path=/; SameSite=Lax`;
  }, [api]);

  // Initial load + re-load when lang changes (in case some strings come from API).
  useEffect(() => {
    void refresh();
  }, [refresh, lang]);

  // Refresh every 5 minutes as a safety net even if sockets misbehave.
  useEffect(() => {
    const id = setInterval(() => void refresh(), 5 * 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  refreshRef.current = () => void refresh();
  useDataChanged(
    useCallback(() => {
      refreshRef.current();
    }, []),
  );

  return (
    <>
      <HeaderCard summary={summary} lang={lang} />
      <AnnouncementBanner announcements={announcements} />
      <div className="mb-4">
        <Legend />
      </div>
      <div className="space-y-3">
        {rows.map(({ service, details }) => {
          const isOpen = openServiceId === service.id;
          const slot = selectedSlotByService[service.id] ?? null;
          return (
            <div
              key={service.id}
              className="bg-white rounded-xl shadow p-4 clickable"
              onClick={() => setOpenServiceId(isOpen ? null : service.id)}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-slate-700">{service.name}</div>
                <div className="text-xs uppercase text-slate-500">{service.status}</div>
              </div>
              <Timeline
                incidents={details?.incidents ?? []}
                onCellClick={(s) =>
                  setSelectedSlotByService((prev) => ({ ...prev, [service.id]: s }))
                }
              />
              {isOpen && details ? (
                <ServiceDetailsInline
                  service={details.service}
                  incidents={details.incidents}
                  history={details.history}
                  selectedSlot={slot}
                  onClose={() => setOpenServiceId(null)}
                />
              ) : null}
            </div>
          );
        })}
        {rows.length === 0 ? (
          <div className="text-slate-500 text-center py-8">No services yet.</div>
        ) : null}
      </div>
    </>
  );
}
